/**
 * @module services/secs_to_human
 *
 * Collection of date/time formatting utility functions used throughout
 * the application for displaying timestamps, durations, relative times,
 * file sizes, and other human-readable representations.
 */

/**
 * Format an "updated_at" timestamp as a relative time string.
 * Handles both SQLite format ("2025-09-21 17:17:05") and PostgreSQL
 * ISO format ("2025-09-21T17:17:05.000Z") by detecting the presence
 * of a "T" separator.
 *
 * Returns relative descriptions for recent times:
 * - "Just now" for < 1 minute
 * - "X min ago" for < 1 hour
 * - "X hr ago" for < 1 day
 * - "X days ago" for < 30 days
 * - "Month Day" (e.g., "Jan 15") for < 1 year
 * - "Month Day, Year" (e.g., "Jan 15, 2024") for older dates
 *
 * @param {string} updated_at - Timestamp string in SQLite or ISO format
 * @returns {string|null} Human-readable relative time, or null if input is falsy
 */
export const formatUpdateDate = (updated_at) => {
  if (updated_at) {
    // Handle both SQLite format ("2025-09-21 17:17:05") and PostgreSQL ISO format ("2025-09-21T17:17:05.000Z")
    let completeUpdatedAt = updated_at;
    if (!updated_at.includes('T')) {
      // SQLite format - convert to ISO by replacing space with T and appending Z
      completeUpdatedAt = updated_at.replace(" ", "T") + "Z";
    }
    const now = new Date();
    const updated = new Date(completeUpdatedAt);

    // SOLO DATA, NESSUNA ORA. Per la maggior parte degli episodi YouTube il
    // server conosce solo il giorno (upload_date di yt-dlp, "20260929") e lo
    // salva come mezzanotte UTC esatta. Calcolare le ore da quella mezzanotte
    // inventava un'informazione: un video uscito alle 11:00 risultava "18 hr
    // ago" alle 20:38 dello stesso giorno (screenshot del 2026-09-29, Visione
    // TV). Una mezzanotte UTC al millesimo vuol dire "giorno noto, ora no": si
    // confrontano i GIORNI (in UTC, lo stesso calendario di upload_date) e si
    // risponde con la precisione che si ha davvero. I nuovi episodi arrivano
    // con l'ora vera (release_timestamp/timestamp di yt-dlp, vedi
    // storeEpisodeData) e seguono il ramo normale qui sotto.
    if (
      updated.getUTCHours() === 0 && updated.getUTCMinutes() === 0 &&
      updated.getUTCSeconds() === 0 && updated.getUTCMilliseconds() === 0
    ) {
      const giorni = Math.round(
        (Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - updated.getTime()) / 86400000,
      );
      if (giorni <= 0) return "today";
      if (giorni === 1) return "yesterday";
      if (giorni < 30) return `${giorni} days ago`;
      // Oltre un mese vale già la data esatta, calcolata come sotto.
    }

    const diffMs = now - updated;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    // Return progressively less precise descriptions based on elapsed time
    if (diffMins < 1) return "Just now";
    if (diffHours < 1) return `${diffMins} min ago`;
    if (diffDays < 1) return `${diffHours} hr ago`;

    // Less than a month - show days
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

    // Less than a year - show month and day (no year needed)
    if (diffYears < 1) {
      return updated.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    }

    // More than a year - include the year for clarity
    return updated.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  return null
};

/**
 * Convert a number of seconds into a verbose human-readable string.
 * Breaks the value down into years, months, weeks, days, hours, minutes,
 * and seconds, joining them with commas and "and" for the last item.
 *
 * @param {number} seconds - Number of seconds to convert
 * @returns {string} Human-readable duration (e.g., "1 hour and 1 second")
 *   or "Invalid input" for negative/NaN values, or "0 seconds" for zero
 *
 * @example
 * secondsToHumanTime(3661)   // "1 hour and 1 second"
 * secondsToHumanTime(93784)  // "1 day, 2 hours, 3 minutes and 4 seconds"
 */
export function secondsToHumanTime(seconds) {
  if (isNaN(seconds) || seconds < 0) {
    return "Invalid input";
  }

  if (seconds === 0) {
    return "0 seconds";
  }

  // Define time units from largest to smallest with their durations in seconds
  const timeUnits = [
    { unit: "year", seconds: 31536000 },
    { unit: "month", seconds: 2592000 }, // Approximate (30 days)
    { unit: "week", seconds: 604800 },
    { unit: "day", seconds: 86400 },
    { unit: "hour", seconds: 3600 },
    { unit: "minute", seconds: 60 },
    { unit: "second", seconds: 1 },
  ];

  let remainingSeconds = seconds;
  const parts = [];

  // Greedily extract the largest units first
  for (const { unit, seconds: unitSeconds } of timeUnits) {
    if (remainingSeconds >= unitSeconds) {
      const count = Math.floor(remainingSeconds / unitSeconds);
      remainingSeconds %= unitSeconds;
      // Pluralize the unit name when count > 1
      parts.push(`${count} ${unit}${count !== 1 ? "s" : ""}`);
    }
  }

  // Join parts with commas, using "and" before the last item for readability
  if (parts.length > 1) {
    const last = parts.pop();
    return `${parts.join(", ")} and ${last}`;
  }

  return parts[0];
}

/**
 * Convert seconds into a compact clock-style format (M:SS).
 * Unlike secondsToHumanTime, this returns a digital clock format
 * suitable for timers and progress displays.
 *
 * @param {number} seconds - Number of seconds to format
 * @returns {string} Clock-format time (e.g., "2:05") or "Invalid input"
 *
 * @example
 * secondsToClockTime(90)   // "1:30"
 * secondsToClockTime(125)  // "2:05"
 * secondsToClockTime(5)    // "0:05"
 */
export function secondsToClockTime(seconds) {
  if (isNaN(seconds) || seconds < 0) return "Invalid input";

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  // Pad seconds with leading zero for consistent display
  const paddedSecs = secs < 10 ? `0${secs}` : secs;

  return `${mins}:${paddedSecs}`;
}

/**
 * Format a byte count into a human-readable file size string.
 * Uses binary units (1 KB = 1024 bytes).
 *
 * @param {number} bytes - Number of bytes to format
 * @param {number} [decimals=2] - Number of decimal places in the result
 * @returns {string} Formatted size string (e.g., "1.45 MB", "3.20 GB")
 *
 * @example
 * formatBytes(1024)      // "1.00 KB"
 * formatBytes(1234567)   // "1.18 MB"
 */
export const formatBytes = (bytes, decimals = 2) => {
  if (!+bytes) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  // Determine the appropriate unit by taking log base 1024
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat(bytes / Math.pow(k, i)).toFixed(dm)} ${sizes[i]}`;
};

/**
 * Format a timestamp as a 24-hour time string (HH:MM:SS).
 * Used for displaying when events occurred.
 *
 * @param {string|number|Date} timestamp - A value parseable by new Date()
 * @returns {string} Formatted time (e.g., "14:32:07") or "N/A" if falsy
 */
export function formatTime(timestamp) {
  if (!timestamp) return "N/A";

  const date = new Date(timestamp);
  return date.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Format a duration in milliseconds as a compact string (e.g., "2h 3m 45s").
 * Used for displaying job durations and elapsed times.
 *
 * @param {number} ms - Duration in milliseconds
 * @returns {string} Compact duration string or "0s" for zero/negative
 */
export function formatDuration(ms) {
  if (!ms || ms < 0) return "0s";

  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  // Show the two most significant units
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

/**
 * Format a timestamp as a relative time string (e.g., "2 minutes ago").
 * Calculates the difference between now and the given timestamp.
 *
 * @param {number} timestamp - A Unix timestamp in milliseconds
 * @returns {string} Relative time string or "N/A" if falsy
 */
export function formatRelativeTime(timestamp) {
  if (!timestamp) return "N/A";

  // Accept a numeric ms timestamp OR a date string / Date (e.g. an ISO
  // "played_at" from PostgreSQL). Without this, string input made the
  // arithmetic below NaN ("played NaN seconds ago").
  const ts =
    typeof timestamp === "number" ? timestamp : new Date(timestamp).getTime();
  if (Number.isNaN(ts)) return "N/A";

  const now = Date.now();
  const diff = now - ts;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days} day${days !== 1 ? "s" : ""} ago`;
  } else if (hours > 0) {
    return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes !== 1 ? "s" : ""} ago`;
  } else {
    return `${Math.max(0, seconds)} second${seconds !== 1 ? "s" : ""} ago`;
  }
}

/**
 * Format a timestamp as a full date and time string.
 * Uses locale formatting for a readable display (e.g., "Jan 15, 2025, 14:32:07").
 *
 * @param {string|number|Date} timestamp - A value parseable by new Date()
 * @returns {string} Formatted date-time string or "N/A" if falsy
 */
export function formatDateTime(timestamp) {
  if (!timestamp) return "N/A";

  const date = new Date(timestamp);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}

/**
 * Multipurpose display formatter that delegates to either formatDuration
 * or formatDateTime based on the isDuration flag.
 * Used in templates where the display format depends on the data type.
 *
 * @param {string|number|Date} timestamp - The value to format
 * @param {boolean} [isDuration=false] - If true, treat as millisecond duration;
 *   otherwise, treat as a date/time timestamp
 * @returns {string} Formatted string
 */
export function formatForDisplay(timestamp, isDuration = false) {
  if (isDuration) {
    return formatDuration(timestamp);
  } else {
    return formatDateTime(timestamp);
  }
}
